import React, { useMemo } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  useTheme,
  alpha,
  Tooltip
} from '@mui/material';
import {
  CalendarToday,
  EventAvailable,
  EventBusy,
  Today
} from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import { GET_STAFF_SCHEDULES } from '../graphql/queries';
import { format, isToday, isBefore, startOfDay } from 'date-fns';

interface AvailabilityDatePickerProps {
  storeId: string;
  selectedStaffId: string;
  selectedDate: string;
  onDateSelect: (date: string) => void;
  disabled?: boolean;
}

interface StaffAvailabilityDay {
  date: Date;
  hasAvailability: boolean;
  scheduleExists: boolean;
  isWeekend: boolean;
  isPast: boolean;
}

const AvailabilityDatePicker: React.FC<AvailabilityDatePickerProps> = ({
  storeId,
  selectedStaffId,
  selectedDate,
  onDateSelect,
  disabled = false
}) => {
  const theme = useTheme();

  const { data: staffData, loading } = useQuery(GET_STAFF_SCHEDULES, {
    variables: { storeId },
    skip: !storeId,
  });

  const staff = staffData?.allStaff || [];
  const selectedStaff = staff.find((s: any) => s.id === selectedStaffId);

  console.log('🔍 AvailabilityDatePicker Debug:', {
    selectedStaffId,
    hasSelectedStaff: !!selectedStaff,
    schedulesCount: selectedStaff?.schedules?.length || 0,
    loading
  });

  // Generate availability data for the calendar
  const availabilityMap = useMemo((): Map<string, StaffAvailabilityDay> => {
    const map = new Map<string, StaffAvailabilityDay>();
    
    if (!selectedStaff?.schedules) {
      console.log('❌ No staff or schedules found for availability map');
      return map;
    }

    console.log('🗓️ Generating availability map for:', selectedStaff.fullName, 'with', selectedStaff.schedules.length, 'schedules');

    // Generate dates for the next 90 days
    const today = startOfDay(new Date());
    for (let i = 0; i < 90; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      const dateString = format(date, 'yyyy-MM-dd');
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
      const isPast = isBefore(date, today);

      // Check if staff has a schedule for this date
      const hasSchedule = selectedStaff.schedules.some((schedule: any) => {
        // If it's a specific date schedule
        if (schedule.schedule?.date) {
          return schedule.schedule.date === dateString;
        }
        
        // If it's a recurring weekly schedule
        if (schedule.schedule?.weekday !== undefined) {
          return schedule.schedule.weekday === dayOfWeek;
        }
        
        return false;
      });

      map.set(dateString, {
        date,
        hasAvailability: hasSchedule && !isPast,
        scheduleExists: hasSchedule,
        isWeekend,
        isPast
      });
    }

    const availableDays = Array.from(map.values()).filter(day => day.hasAvailability).length;
    console.log('✅ Generated availability map:', availableDays, 'available days');

    return map;
  }, [selectedStaff]);

  // Custom day component to show availability
  const CustomDay = (props: PickersDayProps<Date>) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const dateString = format(day, 'yyyy-MM-dd');
    const availability = availabilityMap.get(dateString);
    const isSelected = selectedDate === dateString;
    const isCurrentDay = isToday(day);

    let dayColor = theme.palette.text.primary;
    let dayBgColor = 'transparent';
    let borderColor = 'transparent';
    let tooltipText = 'No schedule set';

    if (availability?.isPast) {
      dayColor = theme.palette.text.disabled;
      tooltipText = 'Past date';
    } else if (availability?.hasAvailability) {
      dayColor = theme.palette.success.main;
      dayBgColor = alpha(theme.palette.success.main, 0.1);
      borderColor = alpha(theme.palette.success.main, 0.3);
      tooltipText = 'Available for appointments';
    } else if (availability?.scheduleExists) {
      dayColor = theme.palette.warning.main;
      dayBgColor = alpha(theme.palette.warning.main, 0.1);
      borderColor = alpha(theme.palette.warning.main, 0.3);
      tooltipText = 'Schedule exists but may be fully booked';
    } else if (availability?.isWeekend) {
      dayColor = theme.palette.text.secondary;
      tooltipText = 'Weekend - no schedule';
    }

    if (isSelected) {
      dayBgColor = theme.palette.primary.main;
      dayColor = theme.palette.primary.contrastText;
      borderColor = theme.palette.primary.main;
    }

    if (isCurrentDay && !isSelected) {
      borderColor = theme.palette.primary.main;
    }

    return (
      <Tooltip title={tooltipText} arrow placement="top">
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          disabled={disabled || availability?.isPast || outsideCurrentMonth}
          sx={{
            color: dayColor,
            backgroundColor: dayBgColor,
            border: `2px solid ${borderColor}`,
            fontWeight: isSelected ? 700 : isCurrentDay ? 600 : 400,
            '&:hover': {
              backgroundColor: isSelected 
                ? theme.palette.primary.dark 
                : alpha(theme.palette.primary.main, 0.1),
            },
            '&.Mui-disabled': {
              color: theme.palette.text.disabled,
              backgroundColor: 'transparent',
            },
            position: 'relative',
            '&::after': availability?.hasAvailability && !isSelected ? {
              content: '""',
              position: 'absolute',
              bottom: 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: theme.palette.success.main,
            } : {},
          }}
        />
      </Tooltip>
    );
  };

  const selectedDateObj = selectedDate ? new Date(selectedDate + 'T00:00:00') : null;
  const availabilityInfo = selectedDate ? availabilityMap.get(selectedDate) : null;

  const getAvailableCount = () => {
    return Array.from(availabilityMap.values()).filter(day => day.hasAvailability).length;
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="center" minHeight={200}>
          <CircularProgress size={24} sx={{ mr: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading calendar...
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!selectedStaffId) {
    return (
      <Paper sx={{ p: 3, borderRadius: 2, bgcolor: 'background.default' }}>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={200}>
          <CalendarToday sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: 'text.secondary' }}>
            Select a Staff Member
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Choose a staff member to view their available appointment dates
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!selectedStaffId) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2, bgcolor: 'background.default' }}>
        <CalendarToday sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
          Select a Staff Member
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose a staff member to see their available dates.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarToday sx={{ color: 'primary.main' }} />
          Select Appointment Date
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedStaff?.fullName 
            ? `${selectedStaff.fullName}'s availability - ${getAvailableCount()} available days`
            : 'Select a staff member to see available dates'}
        </Typography>

        {/* Legend */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            icon={<EventAvailable />}
            label="Available"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.main,
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          />
          <Chip
            icon={<EventBusy />}
            label="May be booked"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              color: theme.palette.warning.main,
              border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
            }}
          />
          <Chip
            icon={<Today />}
            label="Today"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          />
        </Box>
      </Box>

      {/* Calendar */}
      <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'background.paper' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={selectedDateObj}
            onChange={(newDate) => {
              if (newDate) {
                const dateString = format(newDate, 'yyyy-MM-dd');
                onDateSelect(dateString);
              }
            }}
            disabled={disabled}
            slots={{
              day: CustomDay,
            }}
            sx={{
              '& .MuiPickersCalendarHeader-root': {
                paddingLeft: 2,
                paddingRight: 2,
                marginBottom: 1,
              },
              '& .MuiDayCalendar-header': {
                paddingLeft: 0,
                paddingRight: 0,
              },
              '& .MuiPickersDay-root': {
                margin: 0.25,
              },
            }}
          />
        </LocalizationProvider>
      </Paper>

      {/* Selected date info */}
      {selectedDate && availabilityInfo && (
        <Box sx={{ mt: 2 }}>
          {availabilityInfo.hasAvailability ? (
            <Alert severity="success" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {format(availabilityInfo.date, 'EEEE, MMMM d, yyyy')}
              </Typography>
              <Typography variant="body2">
                This date is available for appointments with {selectedStaff?.fullName}.
              </Typography>
            </Alert>
          ) : availabilityInfo.scheduleExists ? (
            <Alert severity="warning" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {format(availabilityInfo.date, 'EEEE, MMMM d, yyyy')}
              </Typography>
              <Typography variant="body2">
                {selectedStaff?.fullName} has a schedule for this date, but slots may be fully booked.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {format(availabilityInfo.date, 'EEEE, MMMM d, yyyy')}
              </Typography>
              <Typography variant="body2">
                {selectedStaff?.fullName} doesn't have a schedule set for this date.
              </Typography>
            </Alert>
          )}
        </Box>
      )}
    </Box>
  );
};

export default AvailabilityDatePicker;
